import { SimpleCodeExample, AnnotatedCodeExample } from '../types';
import { findLensMatches } from './flexibleLensMatching';

/**
 * Simple tokenizer for basic syntax highlighting
 * This converts plain text into tokens with CSS classes
 */
function tokenizeLine(line: string): Array<{ text: string; className?: string }> {
  const tokens: Array<{ text: string; className?: string }> = [];
  
  // Define token patterns (order matters - more specific first)  
  const patterns = [
    // Strings (must come before other patterns to avoid conflicts)
    { regex: /"[^"]*"/g, className: 'string' },
    // Comments (must come before operators to avoid conflicts with //)
    { regex: /\/\/.*$/g, className: 'comment' },
    // Function calls with :: (like Box::new, String::from)
    { regex: /\b([a-zA-Z_][a-zA-Z0-9_]*::[a-zA-Z_][a-zA-Z0-9_]*)\s*(?=\()/g, className: 'function' },
    // Regular function calls 
    { regex: /\b([a-zA-Z_][a-zA-Z0-9_]*)\s*(?=\()/g, className: 'function' },
    // Rust-specific keywords
    { regex: /\b(fn|let|return|struct|impl|pub|use|mod|if|else|match|for|while|loop|mut|const|static)\b/g, className: 'keyword' },
    // Types
    { regex: /\b(Box|String|User|Vec|Option|Result|i32|u32|i64|u64|usize|isize|f32|f64|bool|char)\b/g, className: 'type-keyword' },
    // Numbers
    { regex: /\b\d+\b/g, className: 'number' },
    // Identifiers (variables, etc.)
    { regex: /\b[a-zA-Z_][a-zA-Z0-9_]*\b/g, className: 'variable' },
    // Operators
    { regex: /[-+*/><=!&|]+/g, className: 'operator' }
  ];
  
  let remaining = line;
  let position = 0;
  
  while (remaining.length > 0) {
    let matchFound = false;
    
    // Try each pattern
    for (const pattern of patterns) {
      pattern.regex.lastIndex = 0; // Reset regex state
      const match = pattern.regex.exec(remaining);
      
      if (match && match.index === 0) {
        // Add the matched token
        let tokenText = match[0];
        let advanceBy = match[0].length;
        
        // Special handling for function names (extract just the function name, not the lookahead)
        if (pattern.className === 'function' && match[1]) {
          tokenText = match[1]; // Just the function name without parentheses/whitespace
          advanceBy = match[1].length; // Only advance by the function name length
        }
        
        tokens.push({
          text: tokenText,
          className: pattern.className
        });
        
        // Move past the match
        remaining = remaining.substring(advanceBy);
        position += advanceBy;
        matchFound = true;
        break;
      }
    }
    
    // If no pattern matched, take one character as plain text
    if (!matchFound) {
      const char = remaining[0];
      const isWhitespace = /\s/.test(char);
      const isPunctuation = /[{}(),;.<>[\]]/.test(char);
      
      tokens.push({
        text: char,
        className: isWhitespace || isPunctuation ? 'text-gray-300' : undefined
      });
      
      remaining = remaining.substring(1);
      position += 1;
    }
  }
  
  return tokens;
}

/**
 * Converts a SimpleCodeExample to a full AnnotatedCodeExample
 * This is where the magic happens - turns simple template into rich data
 */
export function generateAnnotatedCode(example: SimpleCodeExample): AnnotatedCodeExample {
  const lines = example.code.split('\n');
  
  // Debug: Log lens keys we're looking for
  console.log('Lens keys to match:', Object.keys(example.lenses));
  
  return {
    id: example.id,
    title: example.title,
    description: example.description,
    knownLanguage: example.knownLanguage,
    targetLanguage: example.targetLanguage,
    lines: lines.map((lineText, index) => {
      const lineNumber = index + 1;
      const tokens = tokenizeLine(lineText);
      
      // Debug: Log tokens for each line
      console.log(`Line ${lineNumber} tokens:`, tokens.map(t => `"${t.text}" (${t.className})`));
      
      // Apply lens annotations to matching tokens
      const annotatedTokens = tokens.map(token => {
        // Check if this token matches any of our lens elements
        const matchingLensKey = Object.keys(example.lenses).find(lensKey => {
          // Exact match first
          if (token.text === lensKey) return true;
          
          // For compound tokens like "Box::new", check if token contains the lens key
          if (lensKey.includes('::') && token.text === lensKey) return true;
          
          // For simple identifiers like "user", exact match only
          if (!lensKey.includes('::') && token.text === lensKey) return true;
          
          return false;
        });
        
        if (matchingLensKey) {
          const lensData = example.lenses[matchingLensKey];
          console.log(`✅ Matched token "${token.text}" with lens key "${matchingLensKey}"`);
          return {
            ...token,
            annotation: {
              element: matchingLensKey,
              className: token.className || '',
              staticData: lensData,
              position: { line: lineNumber, startChar: 0, endChar: token.text.length }
            }
          };
        }
        
        return token;
      });
      
      return {
        lineNumber,
        tokens: annotatedTokens
      };
    }),
    lensletIds: Object.keys(example.lenses)
  };
}

/**
 * Simple way to create code examples using templates!
 * Auto-generates tokens with syntax highlighting
 */
export function createSimpleRustExample(): SimpleCodeExample {
  return {
    id: 'rust_memory_template',
    title: 'Memory Management in Rust (Template Approach)',
    description: 'Understanding heap allocation from C perspective using templates',
    knownLanguage: 'C',
    targetLanguage: 'Rust',
    code: `fn create_user() -> Box<User> {
    let user = Box::new(User {
        name: String::from("Alice"),
        age: 30,
    });
    user  // No "return" needed
}`,
    lenses: {
      'Box::new': {
        surface: 'malloc(sizeof(User)) → Box::new(User { ... })',
        concept: 'C allocates raw memory vs Rust constructs typed objects on heap',
        paradigm: 'Rust eliminates C memory leaks through ownership and automatic deallocation',
        badge: 'C'
      },
      'user': {
        surface: 'return user; → user',
        concept: 'C requires explicit return vs Rust\'s expression-based returns',
        paradigm: 'Rust eliminates C\'s return bugs through expression-oriented design',
        badge: 'C'
      },
      'String::from': {
        surface: 'strcpy(buffer, "Alice") → String::from("Alice")',
        concept: 'C uses unsafe string copying vs Rust\'s safe string construction',
        paradigm: 'Rust prevents C\'s buffer overflow attacks through owned strings',
        badge: 'C'
      }
    }
  };
}

/**
 * Another example showing different programming concepts
 */
export function createSimpleLoopExample(): SimpleCodeExample {
  return {
    id: 'rust_iteration_template',
    title: 'Safe Iteration in Rust',
    description: 'Understanding iterators from C perspective',
    knownLanguage: 'C',
    targetLanguage: 'Rust',
    code: `fn process_numbers(numbers: Vec<i32>) {
    for num in numbers.iter() {
        if *num > 10 {
            println!("Large: {}", num);
        }
    }
}`,
    lenses: {
      'numbers.iter()': {
        surface: 'for(int i=0; i<len; i++) → numbers.iter()',
        concept: 'C uses manual indexing vs Rust\'s safe iterators',
        paradigm: 'Rust prevents C\'s buffer overruns through iterator bounds checking',
        badge: 'C'
      },
      '*num': {
        surface: 'numbers[i] → *num',
        concept: 'C accesses by index vs Rust dereferences iterator values',
        paradigm: 'Rust eliminates C\'s segmentation faults through automatic bounds checks',
        badge: 'C'
      }
    }
  };
}